import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";
import axios from "axios";

const PodcastList = () => {
  const [podcasts, setPodcasts] = useState([]);
  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  useEffect(() => {
    axios
      .get("/api/podcasts")
      .then((response) => setPodcasts(response.data))
      .catch((error) => console.error(error));
  }, []);

  return (
    <div>
      <div className="min-h-screen bg-gray-900 text-gray-100 px-3">
        <header className="sticky top-0 z-50 bg-[#2a0f2f] border-b border-cyan-800 px-4 py-2 flex items-center justify-between shadow-lg">
          <div className="flex flex-col">
            <div className="text-lg lg:text-xl font-bold text-cyan-400">🎧 Think5Whys Podcast Radio</div>
            <div className="text-[12px] lg:text-lg text-yellow-200">
              Storytelling & Engagement
            </div>
          </div>

          {/* Desktop user controls */}
          <div className="hidden md:flex items-center gap-3">
            {isLoggedIn ? (
              <>
                <div className="text-xs text-left text-cyan-300">{userName}</div>
                <button
                  onClick={handleLogout}
                  className="p-2 mt-2 rounded-md bg-rose-600 hover:bg-rose-700 transition"
                  title="Logout"
                >
                  ⏻
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate("/login")}
                  className="px-3 py-1 rounded-full border border-cyan-700 text-sm text-cyan-200 hover:bg-cyan-900"
                >
                  Login
                </button>
                <button
                  onClick={() => navigate("/signup")}
                  className="px-3 py-1 rounded-full bg-cyan-500 text-white text-sm hover:bg-cyan-400"
                >
                  Register
                </button>
              </>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setIsMobileMenuOpen((s) => !s)}
            className="md:hidden w-6 h-6 flex items-center justify-center text-xl text-cyan-300 bg-cyan-500"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? "×" : "☰"}
          </button>
      </header>

      {/* Mobile menu content */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-[#2a0f2f] border-b border-cyan-800 px-4 py-2 animate-fadeIn">
          {isLoggedIn ? (
            <div className="flex flex-row items-center justify-center gap-3">
              <span className="text-cyan-300 font-medium whitespace-nowrap h-7">
                Welcome, {userName}
              </span>
              <button
                onClick={handleLogout}
                className="rounded-md bg-rose-600 hover:bg-rose-700 w-7 h-7 flex items-center justify-center text-white"
                title="Logout"
              >
                ⏻
              </button>
            </div>
          ) : (
            <div className="flex flex-row items-center justify-center gap-6">
              <Link to="/login" className="text-cyan-300 hover:text-yellow-300">
                Login
              </Link>
              <span className="text-cyan-500">|</span>
              <Link to="/signup" className="text-cyan-300 hover:text-yellow-300">
                Register
              </Link>
            </div>
          )}
        </div>
      )}        

        {/* Podcast List */}
        <div className="flex flex-col gap-6">
          {podcasts.length === 0 ? (
            <p className="text-center text-gray-400">No podcasts available yet.</p>
          ) : (
            podcasts.map((podcast) => (
              <div
                key={podcast._id}
                className="bg-gray-800 rounded-lg shadow-md p-4 sm:p-6 transition hover:shadow-lg hover:border-cyan-500 border border-gray-700"
              >
                {/* Title */}
                <h3 className="text-lg sm:text-xl font-semibold text-cyan-300 mb-1">
                  {podcast.title}
                </h3>

                {/* Description */}
                <p className="text-sm sm:text-base text-gray-300 mb-4">
                  {podcast.description}
                </p>

                {/* Source Type Handling */}
                <div className="flex justify-center">
                  {podcast.sourceType === "youtube" && (
                    <div className="w-full aspect-video max-w-2xl">
                      <iframe
                        className="w-full h-full rounded-lg"
                        src={`https://www.youtube.com/embed/${new URL(
                          podcast.sourceURL
                        ).searchParams.get("v")}`}
                        title={podcast.title}
                        frameBorder="0"
                        allowFullScreen
                      ></iframe>
                    </div>
                  )}

                  {podcast.sourceType === "spotify" && (
                    <iframe
                      src={`https://open.spotify.com/embed/episode/${podcast.sourceURL
                        .split("/")
                        .pop()}`}
                      width="100%"
                      height="152"
                      className="rounded-lg border-none"
                      allow="encrypted-media"
                    ></iframe>
                  )}

                  {podcast.sourceType === "rss" && (
                    <a
                      href={podcast.sourceURL}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-block px-4 py-2 bg-cyan-600 text-white font-medium rounded-lg shadow-md hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-opacity-75"
                    >
                      🎙️ Listen on RSS
                    </a>
                  )}

                  {podcast.sourceType === "uploaded" && (
                    <a
                      href={podcast.sourceURL}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-block px-4 py-2 bg-cyan-600 text-white font-medium rounded-lg shadow-md hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-opacity-75"
                    >
                      ▶️ Watch / Listen
                    </a>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      <footer className="bg-[#2a0f2f] border-t border-cyan-800 text-cyan-300 py-4">
        <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="text-xs lg:text-sm">
            © 2025 Think5Whys Global DeepTech Solutions. All rights reserved.
          </div>
          <div className="flex flex-wrap gap-4 text-xs lg:text-sm">
            <Link to="/about-us" className="text-white hover:text-cyan-400">About</Link>
            <Link to="/privacy-policy" className="text-white hover:text-cyan-400">Privacy Policy</Link>
            <Link to="/terms-of-use" className="text-white hover:text-cyan-400">Terms of Use</Link>
            <Link to="/contact-us" className="text-white hover:text-cyan-400">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PodcastList;
